import React from 'react';
import { cn } from '@/utils/cn';

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: 'default' | 'success' | 'warning' | 'danger' | 'info' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  energyClass?: 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G';
  isWorst?: boolean;
}

const badgeVariants = {
  default: 'bg-gray-100 text-gray-800',
  success: 'bg-green-100 text-green-800',
  warning: 'bg-yellow-100 text-yellow-800',
  danger: 'bg-red-100 text-red-800',
  info: 'bg-blue-100 text-blue-800',
  outline: 'border border-gray-300 text-gray-700 bg-transparent'
};

const badgeSizes = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-1 text-sm',
  lg: 'px-3 py-1.5 text-base'
};

// Couleurs spécifiques pour les classes énergétiques DPE
const energyClassColors = {
  A: 'bg-green-600 text-white',
  B: 'bg-green-500 text-white',
  C: 'bg-yellow-500 text-white',
  D: 'bg-orange-500 text-white',
  E: 'bg-red-500 text-white',
  F: 'bg-red-600 text-white',
  G: 'bg-red-700 text-white'
};

export const Badge: React.FC<BadgeProps> = ({
  variant = 'default',
  size = 'md',
  energyClass,
  isWorst = false,
  className,
  children,
  'aria-label': ariaLabel,
  ...props
}) => {
  // Si une classe énergétique est spécifiée, utiliser les couleurs DPE
  const badgeStyle = energyClass
    ? energyClassColors[energyClass]
    : badgeVariants[variant];

  // Generate accessible label for energy class
  const energyClassLabel = energyClass ? `Classe énergétique ${energyClass}` : undefined;
  const accessibleLabel = ariaLabel || energyClassLabel;

  return (
    <span
      className={cn(
        'inline-flex items-center font-medium rounded-full',
        'transition-all duration-200',
        badgeStyle,
        badgeSizes[size],
        isWorst && 'ring-4 ring-red-500 ring-offset-2',
        className
      )}
      aria-label={accessibleLabel}
      {...props}
    >
      {energyClass || children}
    </span>
  );
};

export default Badge;